import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cbcQuarantineSession } from '../lib/cbcQuarantineSession';
import {
  findCbcFieldKeys,
  uploadedDocumentIdentity,
  nextCbcUploadLockExpiry,
  deriveCbcUploadLocks,
  deriveCbcProcessingDelays,
  CBC_PROCESSING_TIMEOUT_MS,
  CBC_PROCESSING_LOCK_MESSAGE,
  type CbcUploadLock,
} from '../lib/cbcUploadLifecycle';
import {
  deriveHubLanguageState,
  type FieldFileRef,
  type HubLanguageState,
} from '../lib/cbcTranslation';
import {
  CBC_QUARANTINE_STORAGE_NOTICE,
  useCbcQuarantine,
  type PendingCbcPackage,
} from './useCbcQuarantine';
import type { NormalStep } from '../lib/steps';
import {
  useCbcUploadSubmit,
  type CbcStatusHandle,
} from './useCbcUploadSubmit';

export type { PendingCbcPackage } from './useCbcQuarantine';
export type { CbcStatusHandle } from './useCbcUploadSubmit';

export interface UseCbcUploadLifecycleOptions {
  appId: string | number;
  step: NormalStep;
  cbcStatus: CbcStatusHandle;
  /** Stable delegate; must call save(values, { navigate: false }) inside StepForm. */
  autoSubmit: (values: Record<string, unknown>) => Promise<unknown>;
  latestFormValuesRef: React.MutableRefObject<Record<string, unknown>>;
  /** Called when auto-submit fails. */
  onAutoSubmitError: (notice: string) => void;
  /** Called when the quarantine cache fails (distinct from auto-submit failure). */
  onPersistError: (notice: string) => void;
  readOnly?: boolean;
  mountedRef: React.MutableRefObject<boolean>;
}

export interface UseCbcUploadLifecycleResult {
  uploadLocks: CbcUploadLock[];
  processingDelays: string[];
  hubLang: HubLanguageState;
  /** Seed identities and restore the reload-scoped quarantine cache. */
  hydrate: (
    mergedValues: Record<string, unknown>,
    shouldApply?: () => boolean,
  ) => Promise<void>;
  onFormChange: (data: Record<string, unknown>) => void;
  onSubmissionSucceeded: () => void;
}

/**
 * Observer/composition hook: field discovery, identity tracking, one hydrate()
 * boundary, AdvancedFileUpload observation, and lock-deadline timer.
 */
export function useCbcUploadLifecycle({
  appId,
  step,
  cbcStatus: cbc,
  autoSubmit,
  latestFormValuesRef,
  onAutoSubmitError,
  onPersistError,
  readOnly,
  mountedRef,
}: UseCbcUploadLifecycleOptions): UseCbcUploadLifecycleResult {
  const cbcFieldKeys = useMemo(() => findCbcFieldKeys(step.stepConfig), [step.stepConfig]);

  const hydrateGenerationRef = useRef(0);

  const {
    visiblePackages,
    pendingPackages,
    hydrate: hydrateQuarantine,
    addPending,
    updateBaseline,
    releaseLock,
    removePending,
    onSubmissionSucceeded,
  } = useCbcQuarantine({
    appId,
    stepKey: step.stepKey,
    hubPackages: cbc.packages,
    onPersistError,
  });

  // Field revision: advances on add/remove so lock/delay/hubLang recompute immediately.
  const [fieldRevision, setFieldRevision] = useState(0);

  // Timer epoch: advances when a deadline elapses so projections recompute
  // even if query data hasn't changed.
  const [timerEpoch, setTimerEpoch] = useState(0);
  const bumpFieldRevision = useCallback(
    () => setFieldRevision((revision) => revision + 1),
    [],
  );

  const { seedKnownIdentities, onFormChange } = useCbcUploadSubmit({
    cbcFieldKeys,
    cbcStatus: cbc,
    stepKey: step.stepKey,
    readOnly,
    autoSubmit,
    latestFormValuesRef,
    mountedRef,
    onAutoSubmitError,
    onFieldRevision: bumpFieldRevision,
    addPending,
    updateBaseline,
    releaseLock,
    removePending,
  });

  // ── Field file accessor ────────────────────────────────────────────────────
  // Reads from the latest engine form snapshot. Stable within a step load.
  const getCbcFieldFile = useCallback(
    (fieldKey: string): FieldFileRef | undefined => {
      const v = latestFormValuesRef.current[fieldKey];
      const first = Array.isArray(v) ? v[0] : v;
      if (!first) return undefined;
      if (typeof first === 'string') return { url: first };
      const o = first as Record<string, unknown>;
      const str = (x: unknown) => (typeof x === 'string' && x ? x : undefined);
      return {
        url: str(o.url) ?? str(o.downloadUrl),
        fileName: str(o.filename) ?? str(o.fileName) ?? str(o.originalName) ?? str(o.displayName),
      };
    },
    [], // latestFormValuesRef is a ref; its .current never changes identity
  );

  // ── Upload locks ───────────────────────────────────────────────────────────
  const uploadLocks = useMemo<CbcUploadLock[]>(() => {
    const locks = deriveCbcUploadLocks(visiblePackages, step.stepKey, getCbcFieldFile);
    const locked = new Set(locks.map((l) => l.fieldKey));
    for (const [fieldKey, pending] of Object.entries(pendingPackages)) {
      if (
        pending.lockActive &&
        Date.now() - pending.submittedAt < CBC_PROCESSING_TIMEOUT_MS &&
        !locked.has(fieldKey)
      ) {
        locks.push({ fieldKey, message: CBC_PROCESSING_LOCK_MESSAGE });
      }
    }
    return locks;
  }, [visiblePackages, pendingPackages, step.stepKey, getCbcFieldFile, timerEpoch, fieldRevision]);

  // ── Processing delays ──────────────────────────────────────────────────────
  const processingDelays = useMemo(
    () => deriveCbcProcessingDelays(visiblePackages, step.stepKey, getCbcFieldFile),
    [visiblePackages, step.stepKey, getCbcFieldFile, timerEpoch, fieldRevision],
  );

  // ── Hub language/translation state ────────────────────────────────────────
  const hubLang = useMemo(
    () => deriveHubLanguageState(visiblePackages, step.stepKey, getCbcFieldFile),
    [visiblePackages, step.stepKey, getCbcFieldFile, fieldRevision],
  );

  // ── Lock-deadline timer ────────────────────────────────────────────────────
  useEffect(() => {
    const now = Date.now();
    const deadlines = Object.values(pendingPackages)
      .filter((p) => p.lockActive)
      .map((p) => p.submittedAt + CBC_PROCESSING_TIMEOUT_MS)
      .filter((d) => d > now);
    const serverDeadline = nextCbcUploadLockExpiry(visiblePackages, step.stepKey, now);
    if (serverDeadline != null) deadlines.push(serverDeadline);
    if (deadlines.length === 0) return;
    const delay = Math.max(0, Math.min(...deadlines) - now);
    const id = window.setTimeout(() => setTimerEpoch((n) => n + 1), delay + 25);
    return () => window.clearTimeout(id);
  }, [pendingPackages, visiblePackages, step.stepKey, timerEpoch]);

  // ── hydrate ────────────────────────────────────────────────────────────────
  // Single entry point: seeds known identities from merged values and restores
  // the IDB quarantine snapshot. Must be called once after merged values resolve.
  const hydrate = useCallback(
    async (
      mergedValues: Record<string, unknown>,
      shouldApply: () => boolean = () => true,
    ) => {
      const generation = ++hydrateGenerationRef.current;
      if (!shouldApply()) return;
      seedKnownIdentities(mergedValues);
      // Also keep latestFormValuesRef in sync so getCbcFieldFile resolves correctly
      // from the moment the engine mounts with the merged initial values.
      latestFormValuesRef.current = mergedValues;

      let stored: Record<string, PendingCbcPackage> | undefined;
      try {
        stored = await cbcQuarantineSession.load<Record<string, PendingCbcPackage>>(
          appId,
          step.stepKey,
        );
      } catch (err) {
        console.error('[useCbcUploadLifecycle] quarantine load failed:', err);
        onPersistError(CBC_QUARANTINE_STORAGE_NOTICE);
        // Attribution is unknowable without the stored baseline. Fail closed:
        // hide any existing package for attached CBC files, but do not lock them.
        stored = Object.fromEntries(
          cbcFieldKeys.flatMap((fieldKey) => {
            const documentIdentity = uploadedDocumentIdentity(mergedValues[fieldKey]);
            return documentIdentity
              ? [[fieldKey, {
                  documentIdentity,
                  baselineFingerprint: undefined,
                  submittedAt: Date.now(),
                  lockActive: false,
                } satisfies PendingCbcPackage]]
              : [];
          }),
        );
      }
      if (
        !mountedRef.current ||
        generation !== hydrateGenerationRef.current ||
        !shouldApply()
      ) {
        return;
      }
      hydrateQuarantine(mergedValues, cbcFieldKeys, stored);
    },
    [
      appId,
      step.stepKey,
      cbcFieldKeys,
      latestFormValuesRef,
      mountedRef,
      hydrateQuarantine,
      onPersistError,
      seedKnownIdentities,
    ],
  );

  return {
    uploadLocks,
    processingDelays,
    hubLang,
    hydrate,
    onFormChange,
    onSubmissionSucceeded,
  };
}
